import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';

import agentsResource from 'src/actions/agent/agentsResource';
import emailAddressesResource from 'src/actions/email/emailAddressesResource';
import emailDomainsResource from 'src/actions/email/emailDomainsResource';
import emailSenderNamesResource from 'src/actions/email/emailSenderNamesResource';
import useResource, {
  STATUS_LOADING,
  STATUS_SUCCEEDED,
  getConsolidatedState,
  useResourceValues,
} from 'src/presentation/hooks/useResource';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import { useEndpointsWithInboxes } from 'src/presentation/components/pages/shared/hooks/useEndpointsWithInboxes';

const getAgentName = (agents, item) => {
  const agent = find(agents, agent => agent.id === get(item, 'createdBy.userId'));
  return get(agent, 'name', '');
};

type EmailSetupType = { emailDomains; emailAddresses };

export const useEmailsDomainsAddresses = () => {
  const dispatch = useDispatch();

  const [items, setItems] = useState<EmailSetupType>({ emailDomains: [], emailAddresses: [] });
  const [loadingState, setLoadingState] = useState(STATUS_LOADING);
  const { values: agents = [], errors: agentsErrors, loadingState: agentsLoadingState } = useResource(agentsResource);
  const {
    values: rawEndpoints = [],
    loadingState: emailAddressesLoadingState,
    errors: emailAddressesErrors,
  } = useResource(emailAddressesResource);

  const {
    values: rawEmailDomains = [],
    loadingState: domainsLoadingState,
    errors: domainsErrors,
  } = useResource(emailDomainsResource);

  const {
    values: emailSenders,
    loadingState: emailSendersLoadingState,
    errors: emailSendersErrors,
  } = useResourceValues(emailSenderNamesResource);

  const { endpoints, endpointsErrors, loadingState: endpoinsLoadingState } = useEndpointsWithInboxes();
  const errors = {
    emailDomains: domainsErrors,
    emailAddresses: emailAddressesErrors,
    agents: agentsErrors,
    emailSendersErrors: emailSendersErrors,
    endpoints: endpointsErrors,
  };

  const resetErrors = {
    emailDomains: emailDomainsResource.actions.resetErrors,
    emailAddresses: emailAddressesResource.actions.resetErrors,
  };

  useEffect(() => {
    if (domainsLoadingState === STATUS_SUCCEEDED) {
      dispatch(emailSenderNamesResource.effects.fetchAll(rawEmailDomains));
    }
  }, [dispatch, domainsLoadingState, rawEmailDomains]);

  useEffect(() => {
    const loadingState = getConsolidatedState([
      domainsLoadingState,
      emailAddressesLoadingState,
      agentsLoadingState,
      emailSendersLoadingState,
      endpoinsLoadingState,
    ]);
    setLoadingState(loadingState);

    const dataList = [rawEmailDomains, rawEndpoints, agents, emailSenders];
    const hasEmptyData = dataList.some(data => isNil(data));
    if (hasEmptyData) {
      return;
    }

    const emailDomains = rawEmailDomains.map(emailDomain => ({
      ...emailDomain,
      createdAtString: toDateTimeString(emailDomain.createdAt),
      createdByString: getAgentName(agents, emailDomain),
    }));

    const endpointsWithInboxes = endpoints.filter(({ inboxes }) => inboxes);

    const emailAddresses = rawEndpoints
      .filter(endpoint => endpoint.type === 'EMAIL')
      .map(emailAddress => ({
        ...emailAddress,
        entryPointInbox: endpointsWithInboxes.find(({ id }) => id === emailAddress.id)?.inboxes || '',
        senderName: find(emailSenders, sender => sender.address === emailAddress.address)?.senderName || '',
        createdAtString: toDateTimeString(emailAddress.createdAt),
        createdByString: getAgentName(agents, emailAddress),
      }));

    setItems({ emailDomains, emailAddresses });
  }, [
    agents,
    agentsLoadingState,
    domainsLoadingState,
    emailAddressesLoadingState,
    rawEmailDomains,
    rawEndpoints,
    emailSendersErrors,
    emailSenders,
    emailSendersLoadingState,
    endpoints,
    endpoinsLoadingState,
    setItems,
    setLoadingState,
  ]);

  return {
    errors,
    loadingState,
    items,
    resetErrors,
  };
};
